import React, { useRef, useState } from 'react';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import SignaturePad from '../SignaturePad';
import { CheckCircle, Pen } from 'lucide-react';

/**
 * Bloc de signature réutilisable — encapsule le pattern :
 *   "afficher 'Signé' OU canvas SignaturePad avec boutons".
 *
 * Utilisé par IntervenantSignature, ActionEmargementDialog, etc.
 *
 * Props :
 *   signature        : string|null  — valeur actuelle (data URL ou texte 'signed')
 *   onSave(data)     : callback appelé avec la nouvelle signature au moment de Valider
 *   onClear()        : callback appelé quand on efface une signature existante (optionnel)
 *   label            : string       — libellé au-dessus du bloc (optionnel)
 *   icon             : composant lucide-react à afficher devant le label (optionnel)
 *   signedLabel      : string       — texte du badge quand signé (défaut "Signé")
 *   disabled         : boolean      — désactive le bouton "Signer"
 *   disabledMessage  : string       — message affiché quand disabled et pas encore signé
 *   primaryColor     : classe CSS Tailwind pour le bouton Valider (défaut bleu)
 */
const SignatureBlock = ({
  signature,
  onSave,
  onClear,
  label,
  icon: Icon,
  signedLabel = 'Signé',
  disabled = false,
  disabledMessage = '',
  primaryColor = 'bg-blue-600 hover:bg-blue-700',
}) => {
  const [showPad, setShowPad] = useState(false);
  const padRef = useRef(null);

  const handleValidate = () => {
    if (!padRef.current) return;
    const data = padRef.current.save();
    if (data && onSave) {
      onSave(data);
    }
    setShowPad(false);
  };

  return (
    <div className="space-y-3">
      {label && (
        <div className="flex items-center justify-between">
          <Label className="font-medium text-slate-900 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4" />}
            {label}
          </Label>
          {signature && (
            <Badge className="bg-emerald-100 text-emerald-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              {signedLabel}
            </Badge>
          )}
        </div>
      )}

      {disabled && !signature && disabledMessage && (
        <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded-lg">
          {disabledMessage}
        </p>
      )}

      {showPad ? (
        <div className="space-y-3">
          <div className="border rounded-xl bg-white p-2">
            <SignaturePad ref={padRef} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowPad(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button size="sm" onClick={handleValidate} className={`rounded-xl ${primaryColor} text-white`}>
              <CheckCircle className="w-4 h-4 mr-1" />
              Valider
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPad(true)}
            className="rounded-xl"
            disabled={disabled && !signature}
          >
            <Pen className="w-4 h-4 mr-2" />
            {signature ? 'Modifier' : 'Signer'}
          </Button>
          {signature && onClear && (
            <Button variant="ghost" size="sm" onClick={onClear} className="text-red-600">
              Effacer
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SignatureBlock;
